import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

/** Props do componente FileUploadDropzone */
export interface FileUploadDropzoneProps {
  addFiles: (files: File[]) => void;
  hasMultipleFiles?: boolean;
  hasError?: boolean;
  helper?: string;
  /** Tipos de arquivo aceitos (valor do atributo HTML `accept`, ex: ".pdf,.png") */
  accept?: string;
  /** Bloqueia a escolha de arquivo — inclusive o arrastar-e-soltar. */
  disabled?: boolean;
}

// TODO: update @ads/components-react when "at-ring-focus" works properly with focus-within and other pseudo-classes

export const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  addFiles,
  hasMultipleFiles,
  hasError = false,
  helper = undefined,
  accept = undefined,
  disabled = false,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'fileUpload',
  });
  // Ref em vez de `getElementById('file-upload-input')`: o id era fixo, então com dois
  // dropzones na mesma tela todo clique abria o seletor do primeiro — o arquivo caía no
  // campo errado e o campo clicado continuava vazio.
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAddingFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      addFiles(Array.from(files ?? []));
    }
    // Reseta o input depois de repassar os arquivos: sem isso, escolher o mesmo arquivo de
    // novo em seguida não muda o `value` do input aos olhos do navegador, então nenhum
    // `change` dispara na segunda vez — nenhuma request, nenhum toast, nenhum feedback.
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // Soltar arquivo é evento de drag, não de clique: um `<button disabled>` ainda o
    // recebe, então a guarda tem de ser explícita.
    if (disabled) return;
    const files = e.dataTransfer.files;
    if (files) {
      addFiles(Array.from(files ?? []));
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        data-has-error={hasError}
        className={[
          'focus-within:at-ring-focus relative flex w-full cursor-pointer flex-col items-center justify-center gap-micro rounded-sm border-hairline p-xs',
          'data-[has-error=true]:border-ctx-interactive-destructive',
          'data-[has-error=false]:border-ctx-interactive-secondary',
          'disabled:cursor-not-allowed disabled:opacity-medium',
        ].join(' ')}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          disabled={disabled}
          multiple={hasMultipleFiles}
          onChange={handleAddingFiles}
          {...(accept !== undefined ? { accept } : {})}
        />

        {/* Icon */}
        <CloudArrowUpIcon className="size-sm shrink-0 text-ctx-content-title" />

        {/* Description */}
        <p className="flex flex-col gap-nano text-center text-ctx-content-base">
          <span className="ts-subtitle-xxs">
            <span className="text-ctx-content-primary">{t('dropzoneDescription1')}</span>

            <span className="hidden md:inline">{t('dropzoneDescription2.desktop')}</span>
            <span className="inline md:hidden">{t('dropzoneDescription2.mobile')}</span>
          </span>

          {/* Validation details */}
          <span className="ts-paragraph-xxxs md:ts-paragraph-xxs">
            {accept ?? t('dropzoneValidation')}
          </span>
        </p>
      </button>
      {helper && (
        <span className="at-animate-fadeInFromRight at-cursor-text at-text-ctx-content-base at-duration-300 at-ts-caption-xxxs">
          {helper}
        </span>
      )}
    </>
  );
};
